import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ExternalRepairView from '../components/serviceCalls/ExternalRepairView';
import BarcodeScannerDialog from '../components/common/BarcodeScannerDialog';
import { Repair } from '../types/typesRepair';
import { hybridApiBaseUrl } from '../services/hybridFlags';
import { formatDateTime } from '../utils/date';
import { formatRepairStatusForCustomer, normalizeRepairStatusCanonical } from '../utils/repairStatusDisplay';
import { getRepairStatusColors } from '../utils/repairStatusColors';
import { useThemeMode } from '../contexts/ThemeContext';

/** Game Star brand palette — dark / light */
type GsPalette = {
  bg: string;
  bgMid: string;
  card: string;
  cardSoft: string;
  border: string;
  borderStrong: string;
  yellow: string;
  yellowHover: string;
  yellowMuted: string;
  text: string;
  textMuted: string;
  textDim: string;
  onYellow: string;
  pageShadow: string;
  logoSrc: string;
};

const GS_DARK: GsPalette = {
  bg: '#0a0a0a',
  bgMid: '#121212',
  card: '#1a1a1a',
  cardSoft: '#222222',
  border: 'rgba(255,255,255,0.12)',
  borderStrong: 'rgba(255,255,255,0.22)',
  yellow: '#FFD000',
  yellowHover: '#FFE033',
  yellowMuted: 'rgba(255,208,0,0.14)',
  text: '#ffffff',
  textMuted: '#b0b0b0',
  textDim: '#808080',
  onYellow: '#1a1a1a',
  pageShadow: '0 18px 40px rgba(0,0,0,0.45)',
  logoSrc: `${process.env.PUBLIC_URL || ''}/game-star-logo.png`,
};

const GS_LIGHT: GsPalette = {
  bg: '#f3f4f6',
  bgMid: '#e8eaed',
  card: '#ffffff',
  cardSoft: '#f7f7f8',
  border: 'rgba(0,0,0,0.1)',
  borderStrong: 'rgba(0,0,0,0.18)',
  yellow: '#E6BB00',
  yellowHover: '#F0C820',
  yellowMuted: 'rgba(230,187,0,0.14)',
  text: '#1a1a1a',
  textMuted: '#5c5c5c',
  textDim: '#8a8a8a',
  onYellow: '#1a1a1a',
  pageShadow: '0 18px 40px rgba(15, 23, 42, 0.08)',
  logoSrc: `${process.env.PUBLIC_URL || ''}/game-star-logo-on-light.png`,
};

function getGsPalette(mode: 'light' | 'dark'): GsPalette {
  return mode === 'dark' ? GS_DARK : GS_LIGHT;
}

/** Clear Hebrew UI font for title + CTA */
const GS_UI_FONT = '"Heebo", "Assistant", "Segoe UI", Arial, sans-serif';

type TrackMode = 'callNumber' | 'phone';

function digitsOnly(value: string): string {
  return String(value || '').replace(/\D/g, '').trim();
}

function apiBase(): string {
  return String(hybridApiBaseUrl || 'https://api.gamestar.co.il').replace(/\/+$/, '');
}

async function fetchPublicTrackRepair(callNumber: string): Promise<Repair> {
  const url = `${apiBase()}/api/public/track/${encodeURIComponent(callNumber)}`;
  const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
  if (res.status === 404) throw new Error('NOT_FOUND');
  if (res.status === 429) throw new Error('RATE_LIMIT');
  if (!res.ok) throw new Error('REQUEST_FAILED');
  const json = await res.json();
  const data = json?.data;
  if (!data || typeof data !== 'object') throw new Error('INVALID_RESPONSE');
  return data as Repair;
}

async function fetchPublicTrackByPhone(phone: string): Promise<Repair[]> {
  const url = `${apiBase()}/api/public/track-phone/${encodeURIComponent(phone)}`;
  const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
  if (res.status === 404) throw new Error('NOT_FOUND');
  if (res.status === 429) throw new Error('RATE_LIMIT');
  if (!res.ok) throw new Error('REQUEST_FAILED');
  const json = await res.json();
  const data = Array.isArray(json?.data) ? json.data : [];
  return data as Repair[];
}

function TrackRepairListItem({
  repair,
  onSelect,
  gs,
}: {
  repair: Repair;
  onSelect: (repair: Repair) => void;
  gs: GsPalette;
}) {
  const theme = useTheme();
  const statusMode = theme.palette.mode === 'dark' ? 'dark' : 'light';
  const statusLabel = formatRepairStatusForCustomer(repair.status);
  // צבע התג לפי הסטטוס הקנוני (לא לפי הניסוח הידידותי ללקוח) — אחרת אין התאמה בטבלת הצבעים
  // וכל תג היה נופל לאפור ברירת המחדל.
  const statusColors = getRepairStatusColors(
    normalizeRepairStatusCanonical(repair.status) || statusLabel,
    statusMode,
  );
  const device = String(repair.deviceModel || repair.model || '').trim() || 'מכשיר לא צוין';
  const callNo = String(repair.callNumber || '').trim() || '—';
  const received = repair.receivedDate ? formatDateTime(repair.receivedDate) : '—';

  return (
    <Paper
      component="button"
      type="button"
      variant="outlined"
      onClick={() => onSelect(repair)}
      sx={{
        width: '100%',
        textAlign: 'right',
        cursor: 'pointer',
        borderRadius: 2.5,
        px: 1.75,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        border: '1px solid',
        borderColor: gs.border,
        bgcolor: gs.cardSoft,
        color: gs.text,
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease',
        '&:hover': {
          borderColor: gs.yellow,
          boxShadow: `0 6px 18px ${gs.yellowMuted}`,
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap' }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 800,
              color: gs.yellow,
              direction: 'ltr',
              unicodeBidi: 'embed',
              letterSpacing: '0.03em',
            }}
          >
            {callNo}
          </Typography>
          <Chip
            label={statusLabel}
            size="small"
            sx={{
              height: 24,
              fontWeight: 700,
              fontSize: '0.72rem',
              bgcolor: statusColors.bg,
              color: statusColors.text,
              ...(statusColors.border ? { border: statusColors.border } : {}),
            }}
          />
        </Stack>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25, color: gs.text }}>
          {device}
        </Typography>
        <Typography variant="caption" sx={{ color: gs.textMuted }}>
          קליטה: {received}
          {repair.storeName ? ` · ${repair.storeName}` : ''}
        </Typography>
      </Box>
      <ArrowBackIosNewIcon sx={{ fontSize: 16, color: gs.textDim, flexShrink: 0 }} />
    </Paper>
  );
}

const RepairTrackPage: React.FC = () => {
  const { mode: themeMode, toggleMode } = useThemeMode();
  const gs = getGsPalette(themeMode);
  const theme = useTheme();
  /** מסכים צרים מאוד (≈320px, טלפונים ישנים) — ריווח אותיות מלא היה גורם למספר קריאה
   * ארוך (10 ספרות) לגלוש קלות מרוחב שדה הקלט. */
  const isTinyScreen = useMediaQuery(theme.breakpoints.down(360));
  const { callNumber: callNumberParam } = useParams<{ callNumber?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const phoneFromUrl = digitsOnly(searchParams.get('phone') || '');
  const callFromUrl =
    digitsOnly(callNumberParam || '') ||
    digitsOnly(searchParams.get('n') || searchParams.get('callNumber') || '');

  const initialMode: TrackMode = phoneFromUrl.length >= 9 && !callFromUrl ? 'phone' : 'callNumber';
  const initialInput = initialMode === 'phone' ? phoneFromUrl : callFromUrl;

  const [mode, setMode] = useState<TrackMode>(initialMode);
  const [inputValue, setInputValue] = useState(initialInput);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [repair, setRepair] = useState<Repair | null>(null);
  const [repairList, setRepairList] = useState<Repair[]>([]);
  const [scannerOpen, setScannerOpen] = useState(false);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'מעקב תיקונים';
    const metaDescription = document.querySelector('meta[name="description"]');
    const previousDescription = metaDescription?.getAttribute('content') || '';
    if (metaDescription) {
      metaDescription.setAttribute('content', 'מעקב סטטוס תיקון — Game Star');
    }
    return () => {
      document.title = previousTitle;
      if (metaDescription && previousDescription) {
        metaDescription.setAttribute('content', previousDescription);
      }
    };
  }, []);

  useEffect(() => {
    const id = 'gs-heebo-font';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
  }, []);

  const inputDigits = digitsOnly(inputValue);
  const canSubmit =
    mode === 'callNumber' ? inputDigits.length >= 6 : inputDigits.length >= 9;

  const clearResults = () => {
    setRepair(null);
    setRepairList([]);
  };

  const runLookup = useCallback(
    async (raw: string, lookupMode: TrackMode) => {
      const digits = digitsOnly(raw);
      if (lookupMode === 'callNumber' && digits.length < 6) {
        setError('נא להזין מספר קריאה תקין (לפחות 6 ספרות)');
        clearResults();
        return;
      }
      if (lookupMode === 'phone' && digits.length < 9) {
        setError('נא להזין מספר טלפון תקין (לפחות 9 ספרות)');
        clearResults();
        return;
      }

      setLoading(true);
      setError(null);
      clearResults();

      try {
        if (lookupMode === 'callNumber') {
          const row = await fetchPublicTrackRepair(digits);
          setRepair(row);
          const path = `/track/${encodeURIComponent(digits)}`;
          if (window.location.pathname !== path) {
            navigate(path, { replace: true });
          }
        } else {
          const rows = await fetchPublicTrackByPhone(digits);
          if (rows.length === 0) {
            setError('לא נמצאו תיקונים למספר הטלפון הזה.');
            setRepairList([]);
          } else {
            const sorted = [...rows].sort((a, b) => {
              const ta = a.receivedDate ? new Date(a.receivedDate).getTime() : 0;
              const tb = b.receivedDate ? new Date(b.receivedDate).getTime() : 0;
              return tb - ta;
            });
            setRepairList(sorted);
            navigate(`/track?phone=${encodeURIComponent(digits)}`, { replace: true });
          }
        }
      } catch (e) {
        const code = e instanceof Error ? e.message : '';
        if (code === 'NOT_FOUND') {
          setError(
            lookupMode === 'phone'
              ? 'לא נמצאו תיקונים למספר הטלפון הזה.'
              : 'לא נמצאה קריאה עם המספר הזה. בדקו שהמספר מדויק ונסו שוב.',
          );
        } else if (code === 'RATE_LIMIT') {
          setError('יותר מדי ניסיונות. נסו שוב בעוד דקה.');
        } else {
          setError('אירעה שגיאה בטעינת המעקב. נסו שוב בעוד רגע.');
        }
        clearResults();
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  useEffect(() => {
    if (phoneFromUrl.length >= 9) {
      void runLookup(phoneFromUrl, 'phone');
    } else if (callFromUrl.length >= 6) {
      void runLookup(callFromUrl, 'callNumber');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- רק בכניסה לפי URL
  }, []);

  const handleModeChange = (_: React.MouseEvent<HTMLElement>, next: TrackMode | null) => {
    if (!next || next === mode) return;
    setMode(next);
    setInputValue('');
    setError(null);
    clearResults();
    navigate('/track', { replace: true });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void runLookup(inputValue, mode);
  };

  /** תוצאת סריקת ברקוד/QR — מנקה לספרות בלבד (גם אם נסרק URL מלא עם המספר בתוכו) ומחפש מיד. */
  const handleBarcodeScan = useCallback(
    (rawValue: string) => {
      setScannerOpen(false);
      const digits = digitsOnly(rawValue);
      if (!digits) return;
      setMode('callNumber');
      setInputValue(digits);
      setError(null);
      void runLookup(digits, 'callNumber');
    },
    [runLookup],
  );

  const handleSelectFromList = (row: Repair) => {
    setRepair(row);
    setRepairList([]);
    const cn = digitsOnly(String(row.callNumber || ''));
    if (cn) {
      navigate(`/track/${encodeURIComponent(cn)}`, { replace: true });
    }
  };

  const handleBackToList = () => {
    if (mode === 'phone' && inputDigits.length >= 9) {
      setRepair(null);
      void runLookup(inputValue, 'phone');
    } else {
      setRepair(null);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        direction: 'rtl',
        position: 'relative',
        background:
          themeMode === 'dark'
            ? `radial-gradient(ellipse 110% 70% at 50% -15%, rgba(255,208,0,0.12), transparent 55%), linear-gradient(180deg, ${gs.bg} 0%, ${gs.bgMid} 55%, #0d0d0d 100%)`
            : `radial-gradient(ellipse 110% 70% at 50% -15%, rgba(230,187,0,0.16), transparent 55%), linear-gradient(180deg, ${gs.bg} 0%, ${gs.bgMid} 100%)`,
        py: { xs: 4, sm: 6 },
        px: 2,
      }}
    >
      <IconButton
        onClick={toggleMode}
        title={themeMode === 'light' ? 'מעבר למצב כהה' : 'מעבר למצב בהיר'}
        aria-label={themeMode === 'light' ? 'מעבר למצב כהה' : 'מעבר למצב בהיר'}
        sx={{
          position: 'absolute',
          top: { xs: 12, sm: 16 },
          left: { xs: 12, sm: 16 },
          zIndex: 2,
          color: gs.text,
          border: '1px solid',
          borderColor: gs.borderStrong,
          borderRadius: '10px',
          bgcolor: gs.card,
          '&:hover': {
            bgcolor: gs.cardSoft,
            borderColor: gs.yellow,
          },
        }}
      >
        {themeMode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
      </IconButton>

      <Container maxWidth="md">
        <Stack spacing={3} alignItems="stretch">
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: gs.border,
              bgcolor: gs.card,
              boxShadow: gs.pageShadow,
            }}
          >
            <Box
              sx={{
                px: { xs: 2.5, sm: 4 },
                pt: { xs: 3, sm: 3.5 },
                pb: { xs: 2.5, sm: 3 },
                background: `linear-gradient(160deg, ${gs.yellowMuted} 0%, ${gs.card} 55%, ${gs.card} 100%)`,
                borderBottom: '1px solid',
                borderColor: gs.border,
                textAlign: 'center',
              }}
            >
              <Box
                component="img"
                src={gs.logoSrc}
                alt="Game Star"
                sx={{
                  display: 'block',
                  height: { xs: 72, sm: 92 },
                  width: 'auto',
                  maxWidth: { xs: '88%', sm: 320 },
                  mx: 'auto',
                  mb: 2.25,
                  objectFit: 'contain',
                  background: 'transparent',
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  fontFamily: GS_UI_FONT,
                  fontWeight: 700,
                  fontSize: { xs: '1.55rem', sm: '1.9rem' },
                  letterSpacing: '0.01em',
                  lineHeight: 1.35,
                  mb: 0.75,
                  color: gs.text,
                }}
              >
                מעקב תיקון
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  maxWidth: 460,
                  mx: 'auto',
                  lineHeight: 1.55,
                  fontSize: { xs: '0.9rem', sm: '0.95rem' },
                  color: gs.textMuted,
                }}
              >
                חפשו לפי מספר קריאה או מספר טלפון וקבלו עדכון על סטטוס התיקון
              </Typography>
            </Box>

            <Box sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 }, bgcolor: gs.card }}>
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  display: 'flex',
                  // מתחת ל-md אין מספיק רוחב לשלושת האלמנטים בשורה אחת — שדה החיפוש
                  // היה מתכווץ לרוחב 0 (בלתי נראה/בלתי שמיש) בכל טלפון וכמעט כל טאבלט
                  // במאונך. פריסה בעמודה: שורת מצב (קריאה/טלפון) ואז שורת שדה+כפתור.
                  flexDirection: { xs: 'column', md: 'row' },
                  flexWrap: 'nowrap',
                  alignItems: 'stretch',
                  gap: { xs: 1, sm: 1.25 },
                  width: '100%',
                  maxWidth: 920,
                  mx: 'auto',
                }}
              >
                <ToggleButtonGroup
                  exclusive
                  value={mode}
                  onChange={handleModeChange}
                  size="small"
                  sx={{
                    flexShrink: 0,
                    alignSelf: 'stretch',
                    width: { xs: '100%', md: 'auto' },
                    bgcolor: themeMode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
                    borderRadius: 2,
                    p: 0.3,
                    border: '1px solid',
                    borderColor: gs.border,
                    height: 56,
                    '& .MuiToggleButtonGroup-grouped': {
                      height: '100%',
                      flex: { xs: 1, md: '0 0 auto' },
                    },
                    '& .MuiToggleButton-root': {
                      border: 'none',
                      borderRadius: 1.5,
                      px: { xs: 0.85, sm: 1.35 },
                      py: 0,
                      minWidth: 0,
                      height: '100%',
                      fontWeight: 700,
                      fontSize: { xs: '0.78rem', sm: '0.8125rem' },
                      textTransform: 'none',
                      gap: { xs: 0.35, sm: 0.6 },
                      color: gs.textMuted,
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        bgcolor: themeMode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                      },
                      '&.Mui-selected': {
                        bgcolor: gs.yellow,
                        color: gs.onYellow,
                        '&:hover': { bgcolor: gs.yellowHover },
                      },
                    },
                  }}
                >
                  <ToggleButton value="callNumber">
                    <QrCode2OutlinedIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                    מספר קריאה
                  </ToggleButton>
                  <ToggleButton value="phone">
                    <PhoneOutlinedIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                    מספר טלפון
                  </ToggleButton>
                </ToggleButtonGroup>

                {/* שדה + כפתור תמיד יחד בשורה אחת — גם כשה-toggle יורד לשורה משלו במובייל,
                    כדי שהשדה יקבל רוחב אמיתי במקום להיסחט ל-0 */}
                <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', gap: { xs: 1, sm: 1.25 }, flex: 1, minWidth: 0 }}>
                  <TextField
                    fullWidth
                    size="medium"
                    label={mode === 'callNumber' ? 'מספר קריאה' : 'מספר טלפון'}
                    placeholder={
                      mode === 'callNumber'
                        ? 'הזינו מספר בן כ־10 תווים'
                        : 'הזינו מספר טלפון'
                    }
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {mode === 'callNumber' ? (
                            <IconButton
                              onClick={() => setScannerOpen(true)}
                              disabled={loading}
                              title="סריקת ברקוד/QR של הקריאה"
                              aria-label="סריקת ברקוד"
                              size="small"
                              sx={{ p: 0.5, color: gs.textDim, '&:hover': { color: gs.yellow } }}
                            >
                              <QrCode2OutlinedIcon sx={{ fontSize: 20 }} />
                            </IconButton>
                          ) : (
                            <PhoneOutlinedIcon sx={{ color: gs.textDim, fontSize: 20 }} />
                          )}
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      inputMode: 'numeric',
                      dir: 'ltr',
                      // מונע שהדפדפן "יזכור"/יציע השלמה אוטומטית לשדה חיפוש כזה — זה מה שגרם
                      // לרקע הכחול הבוהק של autofill (שלא מתאים לעיצוב הכהה של הדף).
                      autoComplete: 'off',
                      'aria-label': mode === 'callNumber' ? 'מספר קריאה' : 'מספר טלפון',
                      style: {
                        textAlign: 'center',
                        letterSpacing: isTinyScreen ? '0.01em' : '0.06em',
                        fontWeight: 700,
                        fontSize: isTinyScreen ? '0.9rem' : '1rem',
                        color: gs.text,
                      },
                    }}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      '& .MuiInputLabel-root': {
                        color: gs.textMuted,
                        '&.Mui-focused': { color: gs.yellow },
                      },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2.5,
                        bgcolor: gs.cardSoft,
                        minHeight: 56,
                        color: gs.text,
                        '& fieldset': { borderColor: gs.borderStrong },
                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
                        '&.Mui-focused fieldset': { borderColor: gs.yellow, borderWidth: 2 },
                        '&.Mui-disabled': {
                          bgcolor: 'rgba(255,255,255,0.03)',
                          '& fieldset': { borderColor: gs.border },
                        },
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: gs.textDim,
                        opacity: 1,
                      },
                      // דפדפני Chromium כופים רקע autofill (כחול/צהוב בהיר) שמתעלם מ-bgcolor רגיל —
                      // ה-box-shadow הפנימי העצום הוא הדרך היחידה לעקוף את זה.
                      '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active':
                        {
                          WebkitBoxShadow: `0 0 0 1000px ${gs.cardSoft} inset`,
                          WebkitTextFillColor: gs.text,
                          caretColor: gs.text,
                          transition: 'background-color 9999s ease-in-out 0s',
                        },
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading || !canSubmit}
                    startIcon={
                      loading ? (
                        <CircularProgress size={18} color="inherit" thickness={5} />
                      ) : (
                        <SearchIcon sx={{ fontSize: 20 }} />
                      )
                    }
                    sx={{
                      minWidth: { xs: 84, sm: 128 },
                      minHeight: 56,
                      fontFamily: GS_UI_FONT,
                      fontWeight: 600,
                      fontSize: { xs: '0.9rem', sm: '1.05rem' },
                      letterSpacing: '0.02em',
                      borderRadius: 2.5,
                      px: { xs: 1.25, sm: 2.5 },
                      flexShrink: 0,
                      bgcolor: gs.yellow,
                      color: gs.onYellow,
                      boxShadow: '0 8px 20px rgba(255, 208, 0, 0.28)',
                      flexDirection: 'row-reverse',
                      gap: 0.75,
                      textTransform: 'none',
                      '& .MuiButton-startIcon': {
                        margin: 0,
                        marginInline: 0,
                      },
                      '&:hover': {
                        bgcolor: gs.yellowHover,
                        boxShadow: '0 10px 24px rgba(255, 208, 0, 0.36)',
                      },
                      '&.Mui-disabled': {
                        bgcolor: 'rgba(255,255,255,0.08)',
                        color: gs.textDim,
                      },
                    }}
                  >
                    {loading ? 'בודק…' : 'מעקב'}
                  </Button>
                </Box>
              </Box>

              {error && (
                <Alert
                  severity="warning"
                  sx={{
                    mt: 2,
                    maxWidth: 920,
                    mx: 'auto',
                    textAlign: 'right',
                    borderRadius: 2,
                    alignItems: 'center',
                    bgcolor: 'rgba(255,208,0,0.12)',
                    color: gs.text,
                    border: `1px solid ${gs.yellow}`,
                    '& .MuiAlert-icon': { color: gs.yellow },
                  }}
                >
                  {error}
                </Alert>
              )}
            </Box>
          </Paper>

          <BarcodeScannerDialog
            open={scannerOpen}
            title="סריקת ברקוד מספר קריאה"
            onClose={() => setScannerOpen(false)}
            onScan={handleBarcodeScan}
          />

          {repairList.length > 0 && !repair && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: gs.border,
                bgcolor: gs.card,
                px: { xs: 1.75, sm: 2.5 },
                py: { xs: 2, sm: 2.5 },
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5, color: gs.text }}>
                נמצאו {repairList.length} תיקונים
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: gs.textMuted }}>
                מהחדש לישן — לחצו על קריאה לצפייה בפרטים
              </Typography>
              <Stack spacing={1.25}>
                {repairList.map((row) => (
                  <TrackRepairListItem
                    key={String(row.id || row.callNumber)}
                    repair={row}
                    onSelect={handleSelectFromList}
                    gs={gs}
                  />
                ))}
              </Stack>
            </Paper>
          )}

          {repair && (
            <Box sx={{ width: '100%' }}>
              {mode === 'phone' && (
                <Button
                  onClick={handleBackToList}
                  startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    mb: 1.5,
                    fontWeight: 700,
                    flexDirection: 'row-reverse',
                    gap: 0.75,
                    color: gs.yellow,
                    '& .MuiButton-startIcon': { margin: 0 },
                    '&:hover': { bgcolor: gs.yellowMuted },
                  }}
                >
                  חזרה לרשימה
                </Button>
              )}
              <ExternalRepairView
                repair={repair}
                callNumber={repair.callNumber}
                publicTrackMode
              />
            </Box>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default RepairTrackPage;
